/**
 * LİSTESİ EKSİK OLAN IRKLAR
 *
 * Bu ırkların kural metinleri `docs/CONTENT/races.raw.md` içinde tam, ama
 * bazıları "listedeki N şeyden birini seç" der ve O LİSTE metinde yok.
 *
 * NE YAPMIYORUZ: Seçenekleri uydurmuyoruz. Uydurulmuş mekanikler, DM'in kendi
 * sisteminde resmî gibi görünür ve masada güvenilir kullanılır. Bu yüzden
 * seçenek listesi BOŞ bırakılır ve grup `pending: true` ile işaretlenir.
 *
 * NE YAPIYORUZ: Seçim yapısı tamamen kurulur. DM sonradan sadece `options`
 * dizisini doldurur — modeli, testi veya arayüzü değiştirmeye gerek kalmaz.
 * Arayüz bu grupları "liste bekleniyor" olarak gösterir ve OYUNCU HATASI
 * UYARISI ÜRETMEZ.
 */

import type { AbilityKey, ChoiceOption, RaceEntry } from '@tttrpg/rules'

const BOS = {
  abilityBonuses: {},
  asiChoices: {},
  asi: 0,
  asiChoiceOptions: [] as AbilityKey[],
  acBase: 10,
  darkvision: 0,
  cantrips: [],
  feat: null,
  innateSpells: [],
} satisfies Partial<RaceEntry>

// ===========================================================================
// MUTANT — Adaptasyon ve Bedel listeleri bekleniyor
// ===========================================================================

export const MUTANT: RaceEntry = {
  id: 'mutant',
  name: 'Mutant',
  size: 'Orta',
  reach: 5,
  speed: 30,
  ...BOS,
  choices: [
    {
      id: 'mutant-adaptasyonlar',
      label: 'Adaptations',
      labelTr: 'Adaptasyonlar',
      description: 'Mevcut Mutant listesinden 2 Adaptasyon seç.',
      min: 2,
      max: 2,
      pending: true,
      pendingCount: 6,
      options: [],
    },
    {
      id: 'mutant-bedel',
      label: 'Mutation Burden',
      labelTr: 'Mutasyon Bedeli',
      description: 'Listeden 1 Mutasyon Bedeli seç.',
      min: 1,
      max: 1,
      pending: true,
      pendingCount: 4,
      options: [],
    },
    {
      id: 'mutant-yeni-adaptasyon-3',
      label: 'New Adaptation (3)',
      labelTr: 'Yeni Adaptasyon (Seviye 3)',
      description: '3. seviyede listeden 1 yeni adaptasyon daha seç.',
      min: 1,
      max: 1,
      grantedAtLevel: 3,
      pending: true,
      pendingCount: 6,
      options: [],
    },
  ],
  features: [
    {
      id: 'mutant-ilk-mutasyon',
      name: 'İlk Mutasyon',
      grantedAtLevel: 1,
      description: 'Mevcut listeden 2 Adaptasyon ve 1 Mutasyon Bedeli seç.',
    },
    {
      id: 'mutant-yeni-adaptasyon',
      name: 'Yeni Adaptasyon',
      grantedAtLevel: 3,
      description: 'Listeden 1 yeni adaptasyon seç.',
    },
    {
      id: 'mutant-gelismis-mutasyon',
      name: 'Gelişmiş Mutasyon',
      grantedAtLevel: 5,
      description:
        'Adaptasyonlardan birini geliştir: Gece Gözü 120 ft, Doğal Silah 1d8, Su Mutasyonu yüzme 40 ft, Toksik Doku zehirde avantaj vb.',
    },
    {
      id: 'mutant-ikinci-evrim',
      name: 'İkinci Evrim',
      grantedAtLevel: 9,
      description: '1 yeni adaptasyon daha seç veya mevcut birini geliştir.',
    },
    {
      id: 'mutant-kararli-genom',
      name: 'Kararlı Genom',
      grantedAtLevel: 13,
      description: 'Mutasyon bedelinin olumsuz etkisini 1 / LR yok sayabilirsin.',
    },
    {
      id: 'mutant-apex-mutasyon',
      name: 'Apex Mutasyon',
      grantedAtLevel: 17,
      description:
        'Bonus eylem 1 dk, listeden 2 bonus: hız +10, tırmanma/yüzme 30, doğal silah 1d10, AC +1, STR/CON kazanç, karanlık görüş 120 ft. 1 / LR.',
    },
  ],
}

// ===========================================================================
// HAYVAN HALKI — Doğal Özellik listesi bekleniyor
// ===========================================================================

export const HAYVAN_HALKI: RaceEntry = {
  id: 'hayvan-halki',
  name: 'Hayvan Halkı',
  size: 'Orta',
  reach: 5,
  speed: 30,
  ...BOS,
  choices: [
    {
      id: 'hayvan-dogal-ozellikler',
      label: 'Natural Traits',
      labelTr: 'Doğal Özellikler',
      description: 'Hayvan Halkı listesinden 2 Doğal Özellik seç.',
      min: 2,
      max: 2,
      pending: true,
      pendingCount: 8,
      options: [],
    },
    {
      id: 'hayvan-ucuncu-dogal-ozellik-5',
      label: 'Third Natural Trait (5)',
      labelTr: 'Üçüncü Doğal Özellik (Seviye 5)',
      description: '5. seviyede listeden üçüncü bir Doğal Özellik seç.',
      min: 1,
      max: 1,
      grantedAtLevel: 5,
      pending: true,
      pendingCount: 8,
      options: [],
    },
  ],
  features: [
    {
      id: 'hayvan-hayvansal-miras',
      name: 'Hayvansal Miras',
      grantedAtLevel: 1,
      description: 'Listeden 2 Doğal Özellik seç.',
    },
    {
      id: 'hayvan-icgudusel-refleks',
      name: 'İçgüdüsel Refleks',
      grantedAtLevel: 3,
      description: 'Initiative atışında avantaj. PB / LR.',
    },
    {
      id: 'hayvan-gelisen-soy',
      name: 'Gelişen Soy',
      grantedAtLevel: 5,
      description: 'Listeden üçüncü bir Doğal Özellik seç.',
    },
    {
      id: 'hayvan-uzman-avci',
      name: 'Uzman Avcı',
      grantedAtLevel: 9,
      description:
        'Perception, Survival veya Athletics’ten proficiency sahibi olduğun birinde Expertise.',
    },
    {
      id: 'hayvan-suru-bagi',
      name: 'Sürü Bağı',
      grantedAtLevel: 13,
      description: 'Help eylemini Bonus eylem olarak kullan. PB / LR.',
    },
    {
      id: 'hayvan-ilkel-form',
      name: 'İlkel Form',
      grantedAtLevel: 17,
      description:
        'Bonus eylem 1 dk: hız +10, Algı/Hayatta Kalma avantajı, doğal silah en az 1d8, 2 × PB geçici HP. 1 / LR.',
    },
  ],
}

// ===========================================================================
// BÜYÜDOĞMUŞ — Tema listesi bekleniyor
// ===========================================================================

export const BUYUDOGMUS: RaceEntry = {
  id: 'buyudogmus',
  name: 'Büyüdoğmuş',
  size: 'Orta',
  reach: 5,
  speed: 30,
  ...BOS,
  choices: [
    {
      id: 'buyudogmus-tema',
      label: 'Theme',
      labelTr: 'Tema',
      description:
        'Temana uygun cantrip ve büyü havuzunu belirler. Hangi temaların olduğu kural metninde belirtilmemiş.',
      min: 1,
      max: 1,
      pending: true,
      pendingCount: 4,
      options: [],
    },
  ],
  features: [
    {
      id: 'buyudogmus-buyu-kivilcimi',
      name: 'Büyü Kıvılcımı',
      grantedAtLevel: 1,
      description: 'Temana uygun 1 cantrip öğren.',
    },
    {
      id: 'buyudogmus-ilk-dogal-buyu',
      name: 'İlk Doğal Büyü',
      grantedAtLevel: 3,
      description: 'Temana uygun 1. seviye büyü; 1 / LR yuvasız.',
      innateSpells: [
        { grantedAtLevel: 3, spellIds: ['species-1'], uses: { count: 1, recharge: 'long' } },
      ],
    },
    {
      id: 'buyudogmus-ikinci-dogal-buyu',
      name: 'İkinci Doğal Büyü',
      grantedAtLevel: 5,
      description: 'Temana uygun 2. seviye büyü; 1 / LR yuvasız.',
      innateSpells: [
        { grantedAtLevel: 5, spellIds: ['species-2'], uses: { count: 1, recharge: 'long' } },
      ],
    },
    {
      id: 'buyudogmus-derinlesen-oz',
      name: 'Derinleşen Öz',
      grantedAtLevel: 9,
      description: 'Temana uygun 3. seviye büyü; 1 / LR yuvasız.',
      innateSpells: [
        { grantedAtLevel: 9, spellIds: ['species-3'], uses: { count: 1, recharge: 'long' } },
      ],
    },
    {
      id: 'buyudogmus-buyusel-beden',
      name: 'Büyüsel Beden',
      grantedAtLevel: 13,
      description:
        'Temana uygun 4. seviye büyü; 1 / LR yuvasız. Arkan Bilgi proficiency varsa expertise.',
      innateSpells: [
        { grantedAtLevel: 13, spellIds: ['species-4'], uses: { count: 1, recharge: 'long' } },
      ],
    },
    {
      id: 'buyudogmus-canli-buyu',
      name: 'Canlı Büyü',
      grantedAtLevel: 17,
      description:
        'Temana uygun 5. seviye büyü; 1 / LR yuvasız. LR sonunda bir yuvasız büyü kullanımı geri gelir.',
      innateSpells: [
        { grantedAtLevel: 17, spellIds: ['species-5'], uses: { count: 1, recharge: 'long' } },
      ],
    },
  ],
}

// ===========================================================================
// ÇATLAKDOĞAN — Üç ayrı seçim listesi bekleniyor
// ===========================================================================

export const CATLAKDOGAN: RaceEntry = {
  id: 'catlakdogan',
  name: 'Çatlakdoğan',
  size: 'Orta',
  reach: 5,
  speed: 30,
  ...BOS,
  choices: [
    {
      id: 'catlak-hareket-savunma',
      label: 'Movement/Defense Trait',
      labelTr: 'Hareket/Savunma Özelliği',
      description: 'Listeden 1 Hareket/Savunma özelliği seç.',
      min: 1,
      max: 1,
      pending: true,
      pendingCount: 5,
      options: [],
    },
    {
      id: 'catlak-sosyal-algisal-buyusel',
      label: 'Social/Perceptual/Magical Trait',
      labelTr: 'Sosyal/Algısal/Büyüsel Özellik',
      description: 'Listeden 1 Sosyal/Algısal/Büyüsel özellik seç.',
      min: 1,
      max: 1,
      pending: true,
      pendingCount: 6,
      options: [],
    },
    {
      id: 'catlak-bedel',
      label: 'Rift Burden',
      labelTr: 'Bedel',
      description: 'Listeden 1 Bedel seç.',
      min: 1,
      max: 1,
      pending: true,
      pendingCount: 5,
      options: [],
    },
    {
      id: 'catlak-ikinci-evrim-9',
      label: 'Second Rift Evolution (9)',
      labelTr: 'İkinci Çatlak Evrimi (Seviye 9)',
      description: 'Listedeki ikinci evrim seçeneklerinden 1 tane.',
      min: 1,
      max: 1,
      grantedAtLevel: 9,
      pending: true,
      pendingCount: 6,
      options: [],
    },
    {
      id: 'catlak-avatar-secimi-17',
      label: 'Rift Avatar (17)',
      labelTr: 'Çatlak Avatarı (Seviye 17)',
      description: '1 dk için 2 bonus seç.',
      min: 2,
      max: 2,
      grantedAtLevel: 17,
      pending: true,
      pendingCount: 6,
      options: [],
    },
  ],
  features: [
    {
      id: 'catlak-catlak-bedeni',
      name: 'Çatlak Bedeni',
      grantedAtLevel: 1,
      description: '1 Hareket/Savunma, 1 Sosyal/Algısal/Büyüsel özellik, 1 Bedel seç.',
    },
    {
      id: 'catlak-catlak-sezgisi',
      name: 'Çatlak Sezgisi',
      grantedAtLevel: 3,
      description:
        'Portal/planar rift fark etme denemelerinde avantaj; yoksa ilgili beceride proficiency.',
    },
    {
      id: 'catlak-kararli-mutasyon',
      name: 'Kararlı Mutasyon',
      grantedAtLevel: 5,
      description:
        'Seviye 1’deki hareket/savunma özelliğini geliştir: Kısa Sıçrama 30 ft, Doğal Zırh → zorla hareket avantajı, Element Direnci, Tırmanıcı 40 ft, Süzülme 60 ft.',
    },
    {
      id: 'catlak-ikinci-catlak-evrimi',
      name: 'İkinci Çatlak Evrimi',
      grantedAtLevel: 9,
      description:
        '1 seç: 30 ft telepati / karanlık görüş 120 ft / ek cantrip / beceri proficiency / +2 AC (PB / LR) / +10 ft teleport menzili.',
    },
    {
      id: 'catlak-uzamsal-yarik',
      name: 'Uzamsal Yarık',
      grantedAtLevel: 13,
      description: 'Kendin ve dokunduğun gönüllü yaratıkla 60 ft teleport. 1 / LR.',
    },
    {
      id: 'catlak-catlak-avatar',
      name: 'Çatlak Avatarı',
      grantedAtLevel: 17,
      description:
        'Bonus eylem 1 dk, 2 bonus: uçuş 30 ft / AC +2 / tüm hızlar +10 / hasar direnci / spell saldırılarında avantaj / teleport sonrası ilk saldırıda avantaj. 1 / LR.',
    },
  ],
}

// ===========================================================================
// VAMPİR — Alt Soy listesi bekleniyor
// ===========================================================================

const VAMPIR_YAKINLIK: ChoiceOption[] = [
  {
    id: 'stealth',
    name: 'Stealth',
    nameTr: 'Gizlilik',
    description: 'Gizlilik becerisinde proficiency.',
    effects: { skillProficiencies: ['skill.stealth'] },
  },
  {
    id: 'perception',
    name: 'Perception',
    nameTr: 'Algı',
    description: 'Algı becerisinde proficiency.',
    effects: { skillProficiencies: ['skill.perception'] },
  },
]

export const VAMPIR: RaceEntry = {
  id: 'vampir',
  name: 'Vampir',
  size: 'Orta',
  reach: 5,
  speed: 30,
  ...BOS,
  darkvision: 120,
  choices: [
    {
      id: 'vampir-yakinlik',
      label: 'Night Child (Skill)',
      labelTr: 'Gece Çocuğu (Beceri)',
      description: 'Stealth veya Perception’dan birinde proficiency seç.',
      min: 1,
      max: 1,
      options: VAMPIR_YAKINLIK,
    },
    {
      id: 'vampir-alt-soy',
      label: 'Vampire Subspecies',
      labelTr: 'Vampir Alt Soyu',
      description: 'Alt soyların listesi kural metninde belirtilmemiş.',
      min: 1,
      max: 1,
      pending: true,
      pendingCount: 5,
      options: [],
    },
  ],
  features: [
    {
      id: 'vampir-gece-cocugu',
      name: 'Gece Çocuğu',
      grantedAtLevel: 1,
      description:
        'Karanlık görüş 120 ft. Stealth veya Perception proficiency. Vampir Isırığı 1d6 delici. Özel Beslenme PB / LR: PB kadar HP veya sonraki d20 teste 1d4 Kızıl Öz. Bir Vampir Alt Soyu seç.',
    },
    {
      id: 'vampir-golge-sicramasi',
      name: 'Gölge Sıçrayışı',
      grantedAtLevel: 3,
      description:
        'Loş ışık/Karanlık içinden 30 ft gölgeden gölgeye teleport, bonus eylem. PB / LR.',
    },
    {
      id: 'vampir-gece-formu',
      name: 'Gece Formu',
      grantedAtLevel: 5,
      description:
        'Bonus eylem 1 dk: hız +10, tırmanma 30 ft, Akrobasi/Gizlilik avantajı, Isırık 1d8. 1 / LR.',
    },
    {
      id: 'vampir-oz-emisi',
      name: 'Öz Emişi',
      grantedAtLevel: 9,
      description:
        'Beslenme 2 × PB HP veya 1d6 Kızıl Öz bonus. Hedef bir sonraki tur HP kazanamaz (yalnız düşman).',
    },
    {
      id: 'vampir-sis-bedeni',
      name: 'Sis Bedeni',
      grantedAtLevel: 13,
      description:
        'Hasar alınca Reaction: hasarı yarıya indir, 15 ft hareket, grappled olamazsın. 1 / LR.',
    },
    {
      id: 'vampir-gece-hukumdari',
      name: 'Gece Hükümdarı',
      grantedAtLevel: 17,
      description:
        'Bonus eylem 1 dk: uçuş 40 ft, Gölge Sıçrayışı her tur ücretsiz, ilk isabette +1d6 nekrotik, korku bağışıklığı. 1 / LR.',
    },
  ],
}
